import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { queryClient, queryKeys } from '../services/queryClient';
import { worklistAPI, type WorklistItem } from '../services/api/worklistAPI';
import {
  doctorOfficesAPI,
  type DoctorOffice,
  type OfficeMachinePair,
} from '../services/api/doctorOfficesAPI';
import {
  ultrasoundMachineAPI,
  MachineStatus,
  type UltrasoundMachine,
} from '../services/api/ultrasoundMachineAPI';
import { agendaIncludesStatus } from '../utils/worklistMachineUtils';

const WORKLIST_STALE_MS = 30_000;
const WORKLIST_GC_MS = 30 * 60 * 1000;
const WORKLIST_REFETCH_MS = 30_000;

export type WorklistRegistry = {
  offices: DoctorOffice[];
  pairs: OfficeMachinePair[];
  machines: UltrasoundMachine[];
};

function getPatientId(item: WorklistItem): string {
  return item.patient_id || item.patient?.patient_id || '';
}

function getStudyDescription(item: WorklistItem): string {
  return item.study?.study_description || item.study_description || '';
}

/** Filter, sort, and dedupe day agenda items (shared with WorklistComponent). */
export function processDailyAgendaItems(
  items: WorklistItem[],
  agendaDate: string
): WorklistItem[] {
  const filtered = items.filter((item) => {
    if (!agendaIncludesStatus(item.status)) return false;
    if (!item.scheduled_date) return false;
    const itemDate = item.scheduled_date.split('T')[0];
    return itemDate === agendaDate;
  });

  const sorted = filtered.sort((a, b) => {
    const timeA = a.scheduled_time || '00:00';
    const timeB = b.scheduled_time || '00:00';
    return timeA.localeCompare(timeB);
  });

  const seenIds = new Set<string>();
  const seenComposite = new Set<string>();
  return sorted.filter((item) => {
    if (item.worklist_item_id) {
      if (seenIds.has(item.worklist_item_id)) return false;
      seenIds.add(item.worklist_item_id);
    }
    const composite = `${getPatientId(item)}|${item.scheduled_time || ''}|${getStudyDescription(item)}`;
    if (seenComposite.has(composite)) return false;
    seenComposite.add(composite);
    return true;
  });
}

async function fetchDailyAgenda(
  date: string,
  physician?: string
): Promise<WorklistItem[]> {
  const query: Parameters<typeof worklistAPI.getWorklistItems>[0] = {
    date_from: date,
    date_to: date,
    limit: 1000,
  };
  if (physician) {
    query.scheduled_physician = physician;
  }

  const items = await worklistAPI.getWorklistItems(query);
  return processDailyAgendaItems(items, date);
}

async function fetchWorklistRegistry(): Promise<WorklistRegistry> {
  const [officesResult, pairsResult, machinesResult] = await Promise.allSettled([
    doctorOfficesAPI.getOffices(),
    doctorOfficesAPI.getAvailablePairs(),
    ultrasoundMachineAPI.listMachines({
      limit: 100,
      status: MachineStatus.ACTIVE,
    }),
  ]);

  return {
    offices: officesResult.status === 'fulfilled' ? officesResult.value : [],
    pairs: pairsResult.status === 'fulfilled' ? pairsResult.value : [],
    machines:
      machinesResult.status === 'fulfilled'
        ? machinesResult.value.machines || []
        : [],
  };
}

export type UseWorklistAgendaOptions = {
  date: string;
  /** Server-side physician filter (medico auto-filter). Empty = all. */
  physician?: string;
  enabled?: boolean;
};

export function useWorklistAgenda({
  date,
  physician = '',
  enabled = true,
}: UseWorklistAgendaOptions) {
  return useQuery({
    queryKey: queryKeys.worklist.agenda(date, physician),
    queryFn: () => fetchDailyAgenda(date, physician || undefined),
    staleTime: WORKLIST_STALE_MS,
    gcTime: WORKLIST_GC_MS,
    refetchInterval: WORKLIST_REFETCH_MS,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled: enabled && Boolean(date),
  });
}

export function useWorklistRegistry(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.worklist.registry,
    queryFn: fetchWorklistRegistry,
    staleTime: 5 * 60 * 1000,
    gcTime: WORKLIST_GC_MS,
    refetchOnWindowFocus: false,
    enabled: options?.enabled ?? true,
  });
}

/** Invalidate agenda after mutations (create/edit/delete/status). */
export function useInvalidateWorklist() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.worklist.all }),
    ]);
}

export function prefetchWorklistWarm(date: string) {
  void queryClient.prefetchQuery({
    queryKey: queryKeys.worklist.agenda(date, ''),
    queryFn: () => fetchDailyAgenda(date),
    staleTime: WORKLIST_STALE_MS,
  });
}
